import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useImpersonation } from "@src/appV2/Auth/Impersonation/context";
import { DeploymentEnvironmentName, environmentConfig } from "@src/appV2/environment";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getClockInTimingInfo } from "@src/appV2/redesign/utils/clockInTiming";
import { type TimekeepingAction } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/constants";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { useCallback } from "react";

interface UseOnClockInParams {
  shift: WorkerShift | undefined;
  shiftState: ShiftStateData | undefined;
  currentDate: Date;
  clockInModalState: UseModalState;
  tooFarAwayModalState: UseModalState;
  tooEarlyModalState: UseModalState;
  badgeVerificationModalState: UseModalState;
  inside: boolean;
  isBadgeVerificationRequired: boolean;
  onClockInInitiated?: () => void;
  onWillOpenClockInModal?: () => void;
}

interface OnClockInOptions {
  /**
   * Skip the badge verification gate and continue with the relevant clock-in flow.
   *
   * Set to `true` after badge verification completes (photo uploaded or skipped).
   * This bypasses the `isBadgeVerificationRequired` check which may still be stale
   * since React Query cache invalidation hasn't triggered a re-render yet.
   */
  skipBadgeVerification?: boolean;
}

export function useOnClockIn({
  shift,
  shiftState,
  currentDate,
  clockInModalState,
  tooFarAwayModalState,
  tooEarlyModalState,
  badgeVerificationModalState,
  inside,
  isBadgeVerificationRequired,
  onClockInInitiated,
  onWillOpenClockInModal,
}: UseOnClockInParams): (options?: OnClockInOptions) => void {
  const { showErrorToast } = useToast();
  const { isImpersonating } = useImpersonation();

  const userTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const facilityTimezone = shift?.facility.tmz ?? userTimezone;

  const shiftStartDate = isDefined(shift)
    ? utcToZonedTime(shift.start, facilityTimezone)
    : undefined;
  const isClockInForbidden =
    !isCapacitorPlatform() &&
    !isImpersonating &&
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION;

  const { isAfterEarliestClockInTime, earliestClockInTime } =
    isDefined(shift) && isDefined(shiftState)
      ? getClockInTimingInfo({
          shiftStart: shift.start,
          shiftState,
          currentDate,
        })
      : { isAfterEarliestClockInTime: undefined, earliestClockInTime: undefined };

  return useCallback(
    ({ skipBadgeVerification = false }: OnClockInOptions = {}) => {
      if (
        !isDefined(shift) ||
        !isDefined(shiftState) ||
        !isDefined(shiftStartDate) ||
        !isDefined(earliestClockInTime)
      ) {
        const action: TimekeepingAction = "clock-in";
        logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION_FAILED, {
          action,
          shiftId: shift?._id ?? "unknown",
          workerId: shift?.agentId ?? "unknown",
          workplaceId: shift?.facilityId,
          error: "missing_required_data",
          missingData: {
            shift: !isDefined(shift),
            shiftState: !isDefined(shiftState),
            shiftStartDate: !isDefined(shiftStartDate),
            earliestClockInTime: !isDefined(earliestClockInTime),
          },
        });

        showErrorToast(
          "We couldn't clock you in right now. Please try again, and if the problem persists, contact support."
        );
        return;
      }

      if (isClockInForbidden) {
        showErrorToast("Clock in is only available on the mobile app.");
        return;
      }

      if (!isAfterEarliestClockInTime) {
        logEvent(APP_V2_USER_EVENTS.CLOCK_IN_TOO_EARLY, {
          shiftId: shift._id,
          workerId: shift.agentId,
          earliestClockInTime: earliestClockInTime.toISOString(),
          facilityTimezone,
          userTimezone,
        });

        tooEarlyModalState.openModal();

        return;
      }

      // Notify that the clock-in flow is starting. Placed after the early returns (missing data,
      // forbidden, too early) so it only fires when a clock-in modal will actually open — whether
      // that's the too-far-away modal, badge verification, or the clock-in modal itself.
      onClockInInitiated?.();

      // Check geofence status before opening clock in modal
      if (!inside) {
        logEvent(APP_V2_USER_EVENTS.CLOCK_IN_OUTSIDE_GEOFENCE, {
          shiftId: shift._id,
          workerId: shift.agentId,
          workplaceId: shift.facilityId,
        });
        tooFarAwayModalState.openModal();
        return;
      }

      // Check if a badge verification is required
      if (isBadgeVerificationRequired && !skipBadgeVerification) {
        badgeVerificationModalState.openModal();
        return;
      }

      onWillOpenClockInModal?.();
      clockInModalState.openModal();
    },
    [
      clockInModalState,
      tooFarAwayModalState,
      tooEarlyModalState,
      badgeVerificationModalState,
      inside,
      isBadgeVerificationRequired,
      isClockInForbidden,
      shift,
      shiftState,
      earliestClockInTime,
      isAfterEarliestClockInTime,
      shiftStartDate,
      showErrorToast,
      facilityTimezone,
      userTimezone,
      onClockInInitiated,
      onWillOpenClockInModal,
    ]
  );
}
